import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { ArrowLeft, Pencil, Trash2, Eye, ThumbsUp, ThumbsDown } from "lucide-react";
import DOMPurify from "dompurify";
import knowledgeService from "../services/knowledgeService";
import ArticleEditorModal from "../components/knowledge/ArticleEditorModal";
import { usePermissions } from "../context/PermissionsContext";
import { fmtDateOnly } from "../utils/datetime";

const ArtigoConhecimento = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canEdit = can("edit", "/base-conhecimento");
  const canDelete = can("delete", "/base-conhecimento");

  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [voted, setVoted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const load = () => {
    setLoading(true);
    setVoted(false);
    knowledgeService
      .get(id)
      .then((data) => setArticle(data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
    knowledgeService.related(id).then((d) => setRelated(Array.isArray(d) ? d : [])).catch(() => setRelated([]));
  };

  useEffect(() => {
    load();
    // eslint pattern do projeto: sem exhaustive-deps
  }, [id]);

  const handleVote = async (vote) => {
    if (voted) return;
    try {
      const updated = await knowledgeService.helpful(id, vote);
      setArticle(updated);
      setVoted(true);
    } catch (e) {
      // silencioso
    }
  };

  const handleUpdate = async (data) => {
    try {
      const updated = await knowledgeService.update(id, data);
      setArticle(updated);
      setIsEditorOpen(false);
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao salvar artigo.");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Excluir o artigo "${article?.title}"?`)) return;
    try {
      await knowledgeService.remove(id);
      navigate("/base-conhecimento");
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao excluir artigo.");
    }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64 text-gray-500">Carregando...</div></Layout>;
  }
  if (!article) {
    return <Layout><div className="flex items-center justify-center h-64 text-gray-500">Artigo não encontrado.</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" aria-label="Voltar" onClick={() => navigate("/base-conhecimento")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{article.title}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                {article.category && (
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 font-medium text-blue-700">{article.category}</span>
                )}
                {article.status !== "publicado" && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700">Rascunho</span>
                )}
                {article.is_public && (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-700">Público</span>
                )}
                <span className="text-gray-400">· {article.author_name || "—"} · {fmtDateOnly(article.updated_at) || ""}</span>
                <span className="flex items-center gap-1 text-gray-400"><Eye size={13} /> {article.views || 0}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button variant="outline" onClick={() => setIsEditorOpen(true)}>
                <Pencil className="w-4 h-4 mr-2" /> Editar
              </Button>
            )}
            {canDelete && (
              <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleDelete}>
                <Trash2 className="w-4 h-4 mr-2" /> Excluir
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            {article.body ? (
              <div
                className="email-surface leading-relaxed break-words"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.body) }}
              />
            ) : (
              <p className="text-gray-500 italic">Sem conteúdo.</p>
            )}
            {article.tags && (
              <div className="mt-4 flex flex-wrap gap-2">
                {article.tags.split(",").map((t) => t.trim()).filter(Boolean).map((t) => (
                  <span key={t} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600">#{t}</span>
                ))}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
              <span className="text-sm text-gray-600">Este artigo foi útil?</span>
              <Button variant="outline" onClick={() => handleVote("yes")} disabled={voted}
                className="h-auto gap-1.5 px-3 py-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                <ThumbsUp size={15} /> Sim ({article.helpful_yes || 0})
              </Button>
              <Button variant="outline" onClick={() => handleVote("no")} disabled={voted}
                className="h-auto gap-1.5 px-3 py-1.5 text-red-700 border-red-200 hover:bg-red-50">
                <ThumbsDown size={15} /> Não ({article.helpful_no || 0})
              </Button>
              {voted && <span className="text-xs text-gray-400">Obrigado pelo feedback!</span>}
            </div>
          </CardContent>
        </Card>

        {related.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Artigos relacionados</h2>
              <div className="divide-y divide-gray-100">
                {related.map((r) => (
                  <button key={r.id} type="button" onClick={() => navigate(`/base-conhecimento/${r.id}`)}
                    className="block w-full text-left py-3 hover:bg-gray-50">
                    <div className="font-medium text-gray-900">{r.title}</div>
                    {r.snippet && <div className="text-sm text-gray-500 line-clamp-1">{r.snippet}</div>}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <ArticleEditorModal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        article={article}
        onSubmit={handleUpdate}
      />
    </Layout>
  );
};

export default ArtigoConhecimento;
